"use client"

import * as React from "react"
import { useMemo } from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"
import { CheckMarkCircleIcon } from "../../icons/check-mark-circle"
import { ExclamationMarkCircleIcon } from "../../icons/exclamation-mark-circle"
import { Label } from "./label"
import { Separator } from "./separator"

type FieldPart = "label" | "description" | "error"

type FieldPartIds = Record<FieldPart, readonly string[]>

type FieldContextValue = {
  controlId: string
  partIds: FieldPartIds
  registerControl(id: string): () => void
  registerPart(part: FieldPart, id: string): () => void
}

type FieldControlProps = {
  id?: string
  "aria-label"?: string
  "aria-labelledby"?: string
  "aria-describedby"?: string
  "aria-errormessage"?: string
  "aria-invalid"?: React.AriaAttributes["aria-invalid"]
}

const FieldContext = React.createContext<FieldContextValue | null>(null)

function mergeAriaIds(...values: Array<string | undefined>) {
  const ids = values.flatMap((value) => value?.split(/\s+/).filter(Boolean) ?? [])
  return ids.length === 0 ? undefined : [...new Set(ids)].join(" ")
}

function useFieldPart(part: FieldPart, id: string, enabled = true) {
  const context = React.useContext(FieldContext)
  const registerPart = context?.registerPart

  React.useLayoutEffect(() => {
    if (!enabled || !registerPart) return
    return registerPart(part, id)
  }, [enabled, id, part, registerPart])

  return context
}

/**
 * Adds the surrounding Field's label/description/error relationships to a
 * custom control. Florisoft controls use this automatically.
 */
function useFieldControlProps<TProps extends FieldControlProps>(props: TProps, options?: { invalid?: boolean }) {
  const context = React.useContext(FieldContext)
  const registerControl = context?.registerControl

  React.useLayoutEffect(() => {
    if (!props.id || !registerControl) return
    return registerControl(props.id)
  }, [props.id, registerControl])

  if (!context) {
    return {
      ...props,
      "aria-invalid": props["aria-invalid"] ?? (options?.invalid || undefined),
    }
  }

  const invalid = props["aria-invalid"] ?? (options?.invalid || undefined)
  const labelledBy =
    props["aria-labelledby"] ?? (props["aria-label"] == null ? mergeAriaIds(...context.partIds.label) : undefined)
  const describedBy = mergeAriaIds(
    props["aria-describedby"],
    ...context.partIds.description,
    invalid ? mergeAriaIds(...context.partIds.error) : undefined
  )

  return {
    ...props,
    id: props.id ?? context.controlId,
    "aria-labelledby": labelledBy,
    "aria-describedby": describedBy,
    "aria-errormessage": props["aria-errormessage"] ?? (invalid ? mergeAriaIds(...context.partIds.error) : undefined),
    "aria-invalid": invalid,
  }
}

function isTabStop(element: HTMLElement) {
  // Hidden form inputs report tabIndex 0 but can never take focus.
  return (
    element.tabIndex >= 0 &&
    !(element instanceof HTMLInputElement && element.type === "hidden") &&
    !(element as HTMLInputElement).disabled &&
    element.closest("[aria-hidden=true],[hidden],[inert]") === null
  )
}

/**
 * Focuses a field control from its `ref`. A control's `ref` points at its ARIA
 * target, which for group controls (`RadioGroup`, `Slider`) is not focusable.
 * Then the element's current tab stop receives focus: the first descendant
 * with `tabIndex >= 0` that is enabled and not `aria-hidden`. Returns true
 * when focus landed inside the element.
 */
function focusFieldControl(element: HTMLElement | null | undefined): boolean {
  if (!element) return false

  const target = isTabStop(element) ? element : Array.from(element.querySelectorAll<HTMLElement>("*")).find(isTabStop)
  target?.focus()
  return element.contains(element.ownerDocument.activeElement)
}

/** A native `fieldset` that groups related fields or a group of checkboxes or radios under a `FieldLegend`. */
function FieldSet({ className, ...props }: React.ComponentProps<"fieldset">) {
  return (
    <fieldset
      data-part="field-set"
      className={cn(
        "flex flex-col gap-4 has-[>[data-part=checkbox-group]]:gap-3 has-[>[data-part=radio-group]]:gap-3",
        className
      )}
      {...props}
    />
  )
}

/** The caption of a `FieldSet`. */
function FieldLegend({
  className,
  variant = "legend",
  ...props
}: React.ComponentProps<"legend"> & {
  /** `legend` is a larger heading for a set of fields; `label` is the size of a field label. Defaults to `legend`. */
  variant?: "legend" | "label"
}) {
  return (
    <legend
      data-part="field-legend"
      data-variant={variant}
      className={cn("mb-1.5 font-semibold data-[variant=label]:text-ui-14 data-[variant=legend]:text-ui-16", className)}
      {...props}
    />
  )
}

/**
 * Stacks several `Field`s with the standard gap. Fields inside respond to the group's width when their `orientation` is
 * `responsive`.
 */
function FieldGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-part="field-group"
      className={cn(
        "group/field-group @container/field-group flex w-full flex-col gap-5 data-[part=checkbox-group]:gap-3 *:data-[part=field-group]:gap-4",
        className
      )}
      {...props}
    />
  )
}

const fieldVariants = cva(
  "group/field flex w-full gap-[var(--fl-layout-input-fields-spacing)] data-[status=error]:text-[var(--fl-color-content-error-primary)] has-[[aria-invalid=true]]:text-[var(--fl-color-content-error-primary)]",
  {
    variants: {
      /**
       * `vertical` puts the label above the control, `horizontal` puts them side by side, and `responsive` is vertical
       * in a narrow `FieldGroup` and horizontal in a wide one. Defaults to `vertical`.
       */
      orientation: {
        vertical: "flex-col *:w-full [&>.sr-only]:w-auto",
        horizontal:
          "flex-row items-center has-[>[data-part=field-content]]:items-start *:data-[part=field-label]:flex-auto has-[>[data-part=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
        responsive:
          "flex-col *:w-full @md/field-group:flex-row @md/field-group:items-center @md/field-group:*:w-auto @md/field-group:has-[>[data-part=field-content]]:items-start @md/field-group:*:data-[part=field-label]:flex-auto [&>.sr-only]:w-auto @md/field-group:has-[>[data-part=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
      },
    },
    defaultVariants: {
      orientation: "vertical",
    },
  }
)

/**
 * Associates a label, a control, a description and an error message: the control gets its label and description by
 * ARIA relationships without ids to wire up by hand. Put a `FieldLabel`, a control, and optionally a `FieldDescription`
 * and `FieldError` inside. `FormField` is the shorter form for the usual layout.
 */
function Field({
  className,
  children,
  orientation = "vertical",
  status = "default",
  ...props
}: React.ComponentProps<"div"> &
  VariantProps<typeof fieldVariants> & {
    /** `error` and `success` color the label and the text of the field. Defaults to `default`. */
    status?: "default" | "success" | "error"
  }) {
  const generatedId = React.useId()
  const generatedControlId = `${generatedId}-control`
  const [registeredControlIds, setRegisteredControlIds] = React.useState<readonly string[]>([])
  const controlId = registeredControlIds[0] ?? generatedControlId
  const [partIds, setPartIds] = React.useState<FieldPartIds>({
    label: [],
    description: [],
    error: [],
  })
  const registerPart = React.useCallback((part: FieldPart, id: string) => {
    setPartIds((current) => (current[part].includes(id) ? current : { ...current, [part]: [...current[part], id] }))
    return () => {
      setPartIds((current) =>
        current[part].includes(id)
          ? { ...current, [part]: current[part].filter((currentId) => currentId !== id) }
          : current
      )
    }
  }, [])
  const registerControl = React.useCallback((id: string) => {
    setRegisteredControlIds((current) => (current.includes(id) ? current : [...current, id]))
    return () => {
      setRegisteredControlIds((current) =>
        current.includes(id) ? current.filter((currentId) => currentId !== id) : current
      )
    }
  }, [])
  const context = React.useMemo<FieldContextValue>(
    () => ({
      controlId,
      partIds,
      registerControl,
      registerPart,
    }),
    [controlId, partIds, registerControl, registerPart]
  )

  return (
    <FieldContext.Provider value={context}>
      <div
        data-part="field"
        data-orientation={orientation}
        data-status={status === "default" ? undefined : status}
        className={cn(fieldVariants({ orientation }), className)}
        {...props}
      >
        {children}
      </div>
    </FieldContext.Provider>
  )
}

/** Groups the label, description and error of a horizontal `Field` into one column beside its control. */
function FieldContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-part="field-content"
      className={cn("group/field-content flex flex-1 flex-col gap-0.5 leading-snug", className)}
      {...props}
    />
  )
}

/** The label of a `Field`; it names the field's control and focuses it on click. */
function FieldLabel({ className, htmlFor, id: providedId, ...props }: React.ComponentProps<typeof Label>) {
  const generatedId = React.useId()
  const id = providedId ?? `${generatedId}-label`
  const field = useFieldPart("label", id)
  return (
    <Label
      data-part="field-label"
      id={id}
      htmlFor={htmlFor ?? field?.controlId}
      className={cn(
        "w-fit group-data-[status=error]/field:text-[var(--fl-color-content-error-primary)] group-has-[[aria-invalid=true]]/field:text-[var(--fl-color-content-error-primary)] group-data-[status=success]/field:text-[var(--fl-color-content-success-default)] group-has-[>[data-part=radio-group-item]]/field:font-normal group-has-[>[data-part=radio-group-item][data-checked]]/field:font-semibold group-has-[>[data-part=checkbox]]/field:font-normal group-has-[>[data-part=checkbox][data-checked]]/field:font-semibold",
        className
      )}
      {...props}
    />
  )
}

/**
 * A label-styled title for a `Field` whose label is not a single control's label, such as a card with several controls.
 */
function FieldTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-part="field-label"
      className={cn(
        "flex w-fit items-center gap-2 text-ui-14 font-semibold group-data-[disabled=true]/field:opacity-50",
        className
      )}
      {...props}
    />
  )
}

/** Help text under or beside a `Field`'s control; the control is described by it. */
function FieldDescription({ className, id: providedId, ...props }: React.ComponentProps<"p">) {
  const generatedId = React.useId()
  const id = providedId ?? `${generatedId}-description`
  useFieldPart("description", id)
  return (
    <p
      data-part="field-description"
      id={id}
      className={cn(
        "text-left text-ui-12 font-normal text-[var(--fl-color-content-default-secondary)] group-data-[orientation=horizontal]/field:text-balance [[data-variant=legend]+&]:-mt-1.5",
        "[&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary",
        className
      )}
      {...props}
    />
  )
}

/** A horizontal divider between fields in a `FieldGroup`, with optional centered text such as "or". */
function FieldSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<"div"> & {
  children?: React.ReactNode
}) {
  return (
    <div
      data-part="field-separator"
      data-content={!!children}
      className={cn("relative -my-2 h-5 text-ui-14", className)}
      {...props}
    >
      <Separator className="absolute inset-0 top-1/2" />
      {children && (
        <span
          className="relative mx-auto block w-fit bg-background px-2 text-muted-foreground"
          data-part="field-separator-content"
        >
          {children}
        </span>
      )}
    </div>
  )
}

/**
 * The error message of a `Field`, announced as an alert and linked to the control while it is invalid. Renders nothing
 * without `children` or errors.
 */
function FieldError({
  className,
  children,
  errors,
  ...props
}: React.ComponentProps<"div"> & {
  /**
   * Error objects to show, usually `field.errors`. Repeated messages show once; several show as a list. `children`
   * replaces them.
   */
  errors?: readonly ({ message?: string } | undefined)[]
}) {
  const content = useMemo(() => {
    if (children) {
      return children
    }

    if (!errors?.length) {
      return null
    }

    const uniqueErrors = [...new Map(errors.map((error) => [error?.message, error])).values()]

    if (uniqueErrors?.length == 1) {
      return uniqueErrors[0]?.message
    }

    return (
      <ul className="ml-4 flex list-disc flex-col gap-1">
        {uniqueErrors.map((error, index) => error?.message && <li key={index}>{error.message}</li>)}
      </ul>
    )
  }, [children, errors])

  const generatedId = React.useId()
  const id = props.id ?? `${generatedId}-error`
  useFieldPart("error", id, Boolean(content))

  if (!content) {
    return null
  }

  return (
    <div
      role="alert"
      data-part="field-error"
      id={id}
      className={cn(
        "flex items-start gap-1 text-ui-12 font-normal leading-[18px] text-[var(--fl-color-content-error-primary)]",
        className
      )}
      {...props}
    >
      <ExclamationMarkCircleIcon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
      <span>{content}</span>
    </div>
  )
}

/** A success message under a `Field`'s control. Renders nothing without `children`. */
function FieldSuccess({ className, children, ...props }: React.ComponentProps<"div">) {
  if (!children) {
    return null
  }

  return (
    <div
      role="status"
      data-part="field-success"
      className={cn(
        "flex items-start gap-1 text-ui-12 font-normal leading-[18px] text-[var(--fl-color-content-success-default)]",
        className
      )}
      {...props}
    >
      <CheckMarkCircleIcon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
      <span>{children}</span>
    </div>
  )
}

type FormFieldProps = Omit<React.ComponentProps<typeof Field>, "children"> & {
  /** The text of the field's label. */
  label: React.ReactNode
  /** Help text shown with the control. */
  description?: React.ReactNode
  /** Usually `field.errors`. Accepts any `{ message?: string }[]`. */
  errors?: readonly ({ message?: string } | undefined)[]
  /** Extra props for the rendered FieldLabel (e.g. className). */
  labelProps?: Omit<React.ComponentProps<typeof FieldLabel>, "children">
  children: React.ReactNode
}

/**
 * Standard label, control, description and error layout over the Field parts.
 * Association comes from Field; this adds no behaviour of its own.
 */
function FormField({
  children,
  description,
  errors,
  label,
  labelProps,
  orientation = "vertical",
  ...props
}: FormFieldProps) {
  const descriptionNode = description ? <FieldDescription>{description}</FieldDescription> : null

  return (
    <Field orientation={orientation} {...props}>
      {orientation === "horizontal" ? (
        <>
          {children}
          <FieldContent>
            <FieldLabel {...labelProps}>{label}</FieldLabel>
            {descriptionNode}
            <FieldError errors={errors} />
          </FieldContent>
        </>
      ) : (
        <>
          <FieldLabel {...labelProps}>{label}</FieldLabel>
          {children}
          {descriptionNode}
          <FieldError errors={errors} />
        </>
      )}
    </Field>
  )
}

export {
  Field,
  FieldLabel,
  FieldDescription,
  FieldError,
  FieldSuccess,
  FieldGroup,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldContent,
  FieldTitle,
  FormField,
  focusFieldControl,
  useFieldControlProps,
}

export type { FieldControlProps, FormFieldProps }
